import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import InnerHTML from '@Components/InnerHTML';
import reduce from 'lodash/reduce';
import map from 'lodash/map';
import { LinkElement } from '@els/els-react--link-element';
import { DOI_URL } from '@Components/Results/constants';
import htmlStripsService from '@Services/HtmlStripsService';

const BOLD_PROCESSOR = (val) => `<b>${val}</b>`;

const JOURNAL_FIELDS_MAPPER = [
  {
    field: 'issueTitle',
    processor: BOLD_PROCESSOR,
  },
  {
    field: 'issueYear',
    processor: BOLD_PROCESSOR,
  },
  {
    field: 'issueVolume',
  },
  {
    field: 'issueNo',
  },
  {
    field: 'articleNo',
  },
  {
    field: 'issuePages',
  },
];

function getDoiLink(doi) {
  return doi ? `${DOI_URL}${htmlStripsService.getTitleWithoutHtml(doi, 'full')}` : '';
}

function concatJournalStringHtml(journal, index, withSquareBrackets = false) {
  const fields = reduce(
    JOURNAL_FIELDS_MAPPER,
    (resMemo, mapper) => {
      const value = journal[mapper.field];
      if (value) resMemo.push(mapper.processor ? mapper.processor(value) : value);
      return resMemo;
    },
    []
  );
  const fieldsString = fields.join(', ');
  const fieldsStringHtml = withSquareBrackets ? `[${fieldsString}]` : fieldsString;

  return fieldsStringHtml ? (
    <InnerHTML
      tag="span"
      data-testid="document-journals"
      className="rx-element-literature"
      key={index}
      html={fieldsStringHtml}
      mode="escape"
    />
  ) : null;
}

function DocumentJournalsComponent({ journals, withDoiLink, doi }) {
  const journalsStringHtml = useMemo(
    () => map(journals, (journal, index) => concatJournalStringHtml(journal, index, !withDoiLink)),
    [journals]
  );

  return withDoiLink ? (
    <div className="document-journals document-journals--with-doi-link">
      {journalsStringHtml}
      {doi ? (
        <>
          ,{' '}
          <LinkElement href={getDoiLink(doi)} isExternal noExternalIcon>
            <InnerHTML html={doi} />
          </LinkElement>
        </>
      ) : null}
    </div>
  ) : (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <>{journalsStringHtml}</>
  );
}

DocumentJournalsComponent.propTypes = {
  journals: PropTypes.array.isRequired,
  withDoiLink: PropTypes.bool,
  doi: PropTypes.string,
};

export default DocumentJournalsComponent;
